import { useState } from 'react';
import { usePrevious } from 'react-delta';
import { useRecoilState, useRecoilValue } from 'recoil';
import CollapseTreeIcon from '@mui/icons-material/UnfoldLessOutlined';
import ExpandTreeIcon from '@mui/icons-material/UnfoldMoreOutlined';

import Button from 'components/common/Button/Button';
import ElementWithTooltip from 'components/common/ElementWithTooltip/ElementWithTooltip';
import Toolbar from 'components/common/Toolbar/Toolbar';
import LogDetailsHeader from 'components/common/LogDetails/LogDetailsHeader';
import LogDetailsFacetsTree from 'components/common/LogDetails/LogDetailsFacetsTree';
import LogDetailsFilter from 'components/common/LogDetails/LogDetailsFilter';
import {
  dataDetailsCardTriggerState,
  getCellDataDetailsRendererState,
  getDetailsDataManagerState,
} from 'components/common/Managers/ViewStateManager.state';
import { Query } from 'common/graphqlTypes';

import { useDetailsData } from 'sdk';
import { DataRow, InspectDrawerRenderer } from 'plugins/types';
import { DATASOURCE } from 'schemas';
import { plugin } from '../plugin';
import DVActionsButton from './DVActionsButton';
import DVLogDetailsSummary from './DVLogDetailsSummary';

import 'components/common/LogDetails/LogDetails.scss';
import './DVLogDetailsRenderer.scss';

const POLLING_INTERVAL = 5_000;

/**
 * This component will figure if the plugin has actions configured for the given dataSource,
 * either through `actions` and `actionsExecutors`, or through `toolbarComponents`, and
 * return the correct Actions Button component
 */
const DataSourceActionsButton = ({
  dataSource,
  values,
  initialData,
}: {
  dataSource?: DATASOURCE;
  values: DataRow;
  initialData?: Partial<Query>;
}) => {
  if (!dataSource) {
    return <></>;
  }

  const ToolbarComponentFromConfig = plugin?.toolbarComponents?.[dataSource];

  if (ToolbarComponentFromConfig) {
    return <ToolbarComponentFromConfig values={values} initialData={initialData} />;
  }

  const { actions, actionsExecutors } = plugin;

  const actionsList = actions?.[dataSource];
  const ActionsExecutor = actionsExecutors?.[dataSource];

  if (!actionsList || !ActionsExecutor) {
    return <></>;
  }

  return (
    <DVActionsButton
      actionsExecutor={ActionsExecutor}
      actionsList={actionsList}
      label="Actions"
      values={values}
      initialData={initialData}
    />
  );
};

type TreeExpansionButtonProps = {
  isExpanded: boolean;
  onClick: () => void;
  isDisabled?: boolean;
};

const TreeExpansionButton = ({ isExpanded, onClick, isDisabled = false }: TreeExpansionButtonProps) => (
  <ElementWithTooltip tooltip={isExpanded ? 'Collapse all' : 'Expand all'}>
    <Button size="sm" onClick={onClick} disabled={isDisabled} className="DVLogDetailsRenderer__TreeExpansionButton">
      <div className="DVLogDetailsRenderer__TreeExpansionButtonIcon">
        {isExpanded ? <CollapseTreeIcon fontSize="inherit" /> : <ExpandTreeIcon fontSize="inherit" />}
      </div>
    </Button>
  </ElementWithTooltip>
);

/**
 * This component displays a customized details view of a Deep Visibility event.
 *
 * @prop onClose               function that closes the view
 * @prop onTogglePinned        function that toggles the pinned state
 * @prop logEntry              the currently selected log line whose field/value pairs are shown
 * @prop isPinned              if true, view is in pinned state
 * @prop continuationToken     unique identifier representing the currently selected log line
 */
export const DVLogDetailsRenderer: InspectDrawerRenderer = ({
  continuationToken,
  isPinned,
  logEntry,
  onClose,
  onTogglePinned,
  schema,
}) => {
  const { dataSource } = schema || {};
  const [fieldFilter, setFieldFilter] = useState('');
  const [isTreeExpanded, setIsTreeExpanded] = useState(false);
  const dataDetailsRenderer = useRecoilValue(getCellDataDetailsRendererState);
  const getDetailsDataManager = useRecoilValue(getDetailsDataManagerState);
  const [dataDetailsFieldTrigger, setDataDetailsFieldTrigger] = useRecoilState(dataDetailsCardTriggerState);
  const detailsData = useDetailsData();

  // If the current continuationToken is falsy and this component is rendering, it means the drawer is closing and it
  // should continue to display the stale data while it closes.
  const prevLogEntry = usePrevious(logEntry);
  const prevContinuationToken = usePrevious(continuationToken);
  if (!continuationToken) {
    continuationToken = prevContinuationToken;
    logEntry = prevLogEntry;
  }

  const rowData = logEntry?.values || dataDetailsFieldTrigger?.data;
  if (!rowData) {
    return <></>;
  }

  const DetailsDataManager = dataSource && getDetailsDataManager(rowData, dataSource);

  const toggleTreeExpansion = () => {
    setIsTreeExpanded(!isTreeExpanded);
  };

  const toolbarLeftItems = [
    <LogDetailsFilter fieldFilter={fieldFilter} setFieldFilter={setFieldFilter} placeholder="Search log line" />,
    <TreeExpansionButton isExpanded={isTreeExpanded} onClick={toggleTreeExpansion} isDisabled={!!fieldFilter} />,
  ];

  const toolbarRightItems = [
    <DataSourceActionsButton dataSource={dataSource} values={rowData} initialData={detailsData} />,
  ];

  if (dataDetailsRenderer && dataDetailsFieldTrigger) {
    const DataDetailsCard = dataDetailsRenderer(dataDetailsFieldTrigger.type);
    if (DataDetailsCard) {
      return (
        <>
          <DataDetailsCard
            onClose={() => {
              setDataDetailsFieldTrigger(null);
              onClose();
            }}
            values={logEntry?.values || dataDetailsFieldTrigger.data}
            onBack={logEntry ? () => setDataDetailsFieldTrigger(null) : undefined}
            initialData={detailsData}
          />
          {DetailsDataManager && <DetailsDataManager values={rowData} pollInterval={POLLING_INTERVAL} />}
        </>
      );
    }
  }

  if (!logEntry) {
    return <></>;
  }

  return (
    <>
      <LogDetailsHeader
        isPinned={isPinned}
        onClose={onClose}
        onTogglePinned={onTogglePinned}
        title="Inspect Log line"
      />

      <Toolbar className="LogDetailsActionButtons" leftItems={toolbarLeftItems} rightItems={toolbarRightItems} />

      <DVLogDetailsSummary logEntry={logEntry} schema={schema} initialData={detailsData} />

      <LogDetailsFacetsTree
        fieldFilter={fieldFilter}
        isExpanded={isTreeExpanded || !!fieldFilter}
        logEntry={logEntry}
        onClose={onClose}
        schema={schema}
      />

      {DetailsDataManager && <DetailsDataManager values={rowData} pollInterval={POLLING_INTERVAL} />}
    </>
  );
};
